import { useCallback, useMemo } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { ensureExhaustive, typedKeys } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CheckboxGroup } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FilterPassengers.module.scss";
import { useCarsStyles } from "~core-cars/themes";
import { CoreCarsComponent, PassengerFilter } from "~core-cars/types";

export type FilterPassengerProps = {
  onChange: (values: PassengerFilter[]) => void;
  selectedOption: PassengerFilter[];
} & PlatformComponentProps;

const COMPONENT_KEY = CoreCarsComponent.FilterPassenger;

export const FilterPassenger = ({
  onChange,
  selectedOption,
  className,
}: FilterPassengerProps) => {
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();

  const getSpecificationLabel = useCallback(
    (key: PassengerFilter) => {
      switch (key) {
        case "UpToFive":
          return t("core-cars.filters.passengers.options.upToFive");
        case "SixOrMore":
          return t("core-cars.filters.passengers.options.sixOrMore");
        default:
          ensureExhaustive(key);
      }
    },
    [t]
  );

  const items = useMemo(() => {
    return typedKeys(PassengerFilter).map((o) => ({
      label: getSpecificationLabel(o),
      value: o,
    }));
  }, [getSpecificationLabel]);

  return (
    <div className={clsx(block, className)}>
      <CheckboxGroup
        className={cn("checkbox-group")}
        options={items}
        checkedOptions={selectedOption}
        onOptionsChange={onChange}
      />
    </div>
  );
};
